import { useContext, useMemo, useState, type ContextType } from 'react'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { Button, Select } from '../../../Common'
import { ChatContext } from '../../../../context/chatContextCore'
import { ToastContext } from '../../../../context/toastContextCore'
import { useNls } from '../../../../nls/nlsProvider'
import type { Workspace } from '../../../../context/WorkspaceContext'
import type { AnalysisViewMode } from '../../../../types/analysis'
import type { ChartDescriptor, ChartRecord } from '../../charts'
import { sendChartCard, sendModuleCard } from '../../../../services/analysisChatShareService'

const preferredChannelId = (channels: NonNullable<ContextType<typeof ChatContext>>['channelsList']) =>
  channels.find((channel) => channel.slug === 'general')?.id ?? channels[0]?.id ?? ''

export interface SendToChatMenuProps {
  workspace?: Workspace
  descriptor?: ChartDescriptor
  records?: ChartRecord[]
  viewMode?: AnalysisViewMode
  snapshotId?: string
  isReducedViewer?: boolean
  chartId?: string
  chartTitle?: string
  svgElement?: SVGSVGElement | null
  getSvg?: () => SVGSVGElement | null
  onSent?: () => void
}

export const SendToChatMenu = ({
  workspace,
  descriptor,
  records = [],
  viewMode = 'LIVE',
  snapshotId,
  isReducedViewer = false,
  chartId,
  chartTitle,
  svgElement,
  getSvg,
  onSent,
}: SendToChatMenuProps) => {
  const { t } = useNls()
  const toastContext = useContext(ToastContext)
  const showToast = toastContext?.showToast ?? (() => {})
  const chatContext = useContext(ChatContext)
  const channelsList = chatContext?.channelsList ?? []
  const sendMessage = chatContext?.sendMessage
  const [channelId, setChannelId] = useState(() => preferredChannelId(channelsList))
  const [sending, setSending] = useState(false)
  const isCompare = viewMode === 'COMPARE'
  const isCustomChart = Boolean(chartId)
  const availableChannels = channelsList.filter((channel) => !channel.archivedAt && !channel.permanentlyDeletedAt)

  const selectedChannelId = availableChannels.some((channel) => channel.id === channelId)
    ? channelId
    : preferredChannelId(availableChannels)
  const selectedChannel = useMemo(() => availableChannels.find((channel) => channel.id === selectedChannelId), [availableChannels, selectedChannelId])
  const recordPoints = records.flatMap((record) => typeof record.numeric === 'number' ? [record.numeric] : [])
  const disabled = isReducedViewer || (!isCustomChart && isCompare) || !workspace || !selectedChannel || !sendMessage || sending
  const title = isReducedViewer ? t('NLSAnalysisViewerGateTooltip') : (!isCustomChart && isCompare) ? t('NLSAnalysisSendToChatCompareUnavailable') : undefined

  return (
    <span className="analysisSendToChat" title={title}>
      {availableChannels.length > 1 ? (
        <Select
          trigger="labeledPill"
          label={t('NLSAnalysisSendToChatChannelLabel')}
          ariaLabel={t('NLSAnalysisSendToChatChannelLabel')}
          value={selectedChannelId || null}
          disabled={disabled}
          onChange={(val) => setChannelId(val)}
          options={availableChannels.map((channel) => ({ value: channel.id, label: `#${channel.slug}` }))}
        />
      ) : selectedChannel ? <span className="analysisSendToChat__channel">#{selectedChannel.slug}</span> : null}
      <Button variant="secondary" size="sm" icon={<SendRoundedIcon />} disabled={disabled} onClick={() => {
        if (!workspace || !selectedChannel || !sendMessage) return
        if (!isCustomChart && isCompare) return
        setSending(true)

        if (isCustomChart && chartId) {
          const svg = svgElement ?? getSvg?.() ?? null
          void sendChartCard({
            workspace,
            channelId: selectedChannel.id,
            chartId,
            title: chartTitle ?? descriptor?.title ?? 'Chart',
            svgElement: svg,
            sendMessage,
          }).then(() => {
            showToast(t('NLSAnalysisSendToChatSucceeded', { channel: selectedChannel.slug }), 'success')
            onSent?.()
          }).catch(() => {
            showToast(t('NLSAnalysisSendToChatFailed'), 'error')
          }).finally(() => {
            setSending(false)
          })
          return
        }

        if (descriptor) {
          void sendModuleCard({
            workspace,
            channelId: selectedChannel.id,
            moduleId: descriptor.id,
            moduleTitle: descriptor.title,
            records,
            sparkline: recordPoints.length > 0 ? { points: recordPoints } : undefined,
            viewMode: viewMode as Exclude<AnalysisViewMode, 'COMPARE'>,
            snapshotId,
            sendMessage,
          }).then(() => {
            showToast(t('NLSAnalysisSendToChatSucceeded', { channel: selectedChannel.slug }), 'success')
            onSent?.()
          }).catch(() => {
            showToast(t('NLSAnalysisSendToChatFailed'), 'error')
          }).finally(() => {
            setSending(false)
          })
        }
      }}>
        {sending ? t('NLSAnalysisSendToChatSending') : t('NLSAnalysisSendToChatAction')}
      </Button>
      {!selectedChannel && (!isCompare || isCustomChart) ? <span className="analysisSendToChat__empty">{t('NLSAnalysisSendToChatNoChannels')}</span> : null}
    </span>
  )
}
